import { Util } from 'src/shared/util/util';
import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { ToDosQueriesService } from 'src/shared/services/to-dos-queries.service';
import { Genome } from 'src/providers/genome/genome';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';

import { Loading } from 'src/shared/components/loading/loading';

import { RoomModel } from 'src/models/to-do.model';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Logger } from 'src/providers/logger';
import { PhotoCaptionService } from 'src/shared/components/photo-caption/photo-caption.service';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';


@Component({
    templateUrl: 'room.html',
    providers: [PhotoCaptionService],
    standalone: false
})

export class ToDosRoomPage implements OnInit {

  addressId: any;
  editPrio: boolean;
  form: UntypedFormGroup;
  isRightSideContent = true;
  room: RoomModel;
  rooms: RoomModel[];
  errorMessage: string;
  contentHeight: number;
  taskListId: any;
  loaded: boolean;

  private readonly toDosQueries = inject(ToDosQueriesService);

  constructor(
    private fb: UntypedFormBuilder,
    public genome: Genome,
    private modalCtrl: ModalController,
    private navCtrl: CustomNavController,
    public toDos: ToDos,
    private storage: TidyStorage,
    private logger: Logger,
    private photoCaptionService: PhotoCaptionService,
    private rightSidePanelService: RightSidePanelService,
    public util: Util
  ) {
    this.form = this.fb.group({
      addresses: ['', Validators.required]
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.getPageData();
  }

  async getPageData() {
    try {
      this.loaded = false;
      await this.getRoomFromParams();
      const id = this.toDos.getStorageIds();
      this.addressId = id.addressId;
      this.taskListId = id.taskListId;
      if (!this.room) {
        await this.getRooms(id);
      }
      this.rightSidePanelService.setDialogPageTitle(this.room?.name);
      this.loaded = true;
    } catch (_err) {
      // Silent error handling
    }
  }

  async getRoomFromParams() {
    const dialogParams = await this.storage.retrieve('dialog-params');
    this.room = this.navCtrl.getParam('room') || dialogParams?.room;
    this.rooms = this.navCtrl.getParam('rooms') || dialogParams?.rooms;
  }

  async getRooms(id) {
    const rooms = await this.toDos.getListDetail(this.taskListId);
    this.rooms = this.toDos.buildRoomIcons(rooms);
    this.room = this.rooms.find((room) => {
      if (room.id === parseFloat(id.roomId)) {
        return room;
      }
    });
  }

  goToAddToDo() {
    const params = {
      rooms: this.toDos.parseRoomCheckboxItems(this.rooms),
      roomsResponse: this.rooms,
      selectedListId: this.taskListId,
      addressId: this.addressId,
      toDo: null
    }
    this.rightSidePanelService.navigateTo('add-to-do', params);
  }
  
  async deleteGroup() {
    const params: ConfirmPageParamsModel = {
      title: 'Delete Card?',
      body: 'This will delete the card from all checklists. You will not be able to undo this action.',
      backText: 'Go Back',
      confirmText: 'Confirm',
      confirmAction: this.confirmDeleteGroup.bind(this)
    }
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmDeleteGroup() {
    try {
      await this.toDos.deleteGroup(this.room.id, this.taskListId);
      // Invalidate list detail cache after deleting group
      this.toDosQueries.invalidateListDetail(this.taskListId);
      if (this.isRightSideContent) {
        this.rightSidePanelService.navigateTo('to-dos');
      } else {
        this.navCtrl.navigateForward('to-dos');
      }
      this.modalCtrl.dismiss();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  goToEditToDo(toDo) {
    if (toDo.task_type === 'take_before_photo' || toDo.task_type === 'take_after_photo') {
      return;
    }
    const params = {
      toDo,
      roomType: this.room.type,
      roomId: this.room.id
    }
    this.rightSidePanelService.navigateTo('add-to-do', params);
  }

  goToAddPhoto() {
    this.rightSidePanelService.navigateTo('add-room-photo', {});
  }

  goToEditRoomInfo() {
    const params = {
      room: this.room
    }
    this.rightSidePanelService.navigateTo('edit-room-info', params);
  }

  @Loading('', true)
  async takeRoomPhoto() {
    try {
      const newPhotoNotes = await this.photoCaptionService.getMultipleImages('room');
      await this.storage.save('roomPhotoNotes', newPhotoNotes.map(note => note.url));
      const params = {
        card: this.room,
        multiple: true
      }
      this.rightSidePanelService.navigateTo('add-room-photo', params);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.logger.error(err, 'room-photo-error');
    }
  }

  @Loading('', true)
  async updateToDoOrder(orderedToDos) {
    try {
      const tasksInOrderArray = [];
      orderedToDos.map((toDo) => {
        tasksInOrderArray.push(toDo.id);
      });
      const payload = {
        address_task_group_id: this.room.id,
        address_task_ids_in_order: tasksInOrderArray
      }
      await this.toDos.updateToDoOrder(this.taskListId, payload);
      // Invalidate list detail cache after updating task order
      this.toDosQueries.invalidateListDetail(this.taskListId);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }
}
